/**
 * Groups CSV preview rows by the active component-column value for the
 * optional "By component" preview layout on Upload and map.
 */

export const BLANK_COMPONENT_GROUP_LABEL = "(no component)";

export interface PreviewComponentGroup {
  /** Display label (component name, or BLANK_COMPONENT_GROUP_LABEL). */
  label: string;
  /** Original row index in the full preview (0-based). */
  rows: Array<{ rowIndex: number; row: Record<string, string> }>;
}

/**
 * Groups rows by trimmed component-column value, preserving first-seen order.
 * Blank / missing values land in a trailing "(no component)" group.
 * When `componentColumn` is empty, every row is in the blank group.
 */
export function groupPreviewRowsByComponent(
  rows: readonly Record<string, string>[],
  componentColumn: string,
): PreviewComponentGroup[] {
  const named = new Map<string, PreviewComponentGroup["rows"]>();
  const blank: PreviewComponentGroup["rows"] = [];
  const order: string[] = [];

  rows.forEach((row, rowIndex) => {
    const value = componentColumn
      ? (row[componentColumn] ?? "").trim()
      : "";
    if (!value) {
      blank.push({ rowIndex, row });
      return;
    }
    const list = named.get(value);
    if (list) {
      list.push({ rowIndex, row });
    } else {
      named.set(value, [{ rowIndex, row }]);
      order.push(value);
    }
  });

  const groups: PreviewComponentGroup[] = order.map((label) => ({
    label,
    rows: named.get(label) ?? [],
  }));
  if (blank.length > 0) {
    groups.push({ label: BLANK_COMPONENT_GROUP_LABEL, rows: blank });
  }
  return groups;
}

export interface UnrecognizedComponentSummary {
  /** Distinct unknown names (already filtered to names present in the file). */
  unknownNames: readonly string[];
  /** Rows whose component name is in unknownNames. */
  unknownRowCount: number;
  /** Rows with a blank component value when a component column is selected. */
  blankRowCount: number;
}

/**
 * Counts preview rows that will not match a company component: unknown names
 * (no parameters returned) and blank component cells.
 */
export function summarizeUnrecognizedComponents(args: {
  rows: readonly Record<string, string>[];
  componentColumn: string;
  unknownComponentNames: readonly string[];
}): UnrecognizedComponentSummary {
  const unknownSet = new Set(args.unknownComponentNames);
  let unknownRowCount = 0;
  let blankRowCount = 0;

  if (!args.componentColumn) {
    return {
      unknownNames: args.unknownComponentNames,
      unknownRowCount: 0,
      blankRowCount: args.rows.length,
    };
  }

  for (const row of args.rows) {
    const value = (row[args.componentColumn] ?? "").trim();
    if (!value) {
      blankRowCount += 1;
    } else if (unknownSet.has(value)) {
      unknownRowCount += 1;
    }
  }

  return {
    unknownNames: args.unknownComponentNames,
    unknownRowCount,
    blankRowCount,
  };
}

export function isUnrecognizedPreviewGroup(
  label: string,
  unknownComponentNames: ReadonlySet<string>,
): boolean {
  return (
    label === BLANK_COMPONENT_GROUP_LABEL || unknownComponentNames.has(label)
  );
}
